<script lang="ts">
  import { fade } from 'svelte/transition';
  import { route, defineRoutes } from '$router/router.svelte';
  import { session } from '$domain/session.svelte';
  import { flagString } from '$domain/flags.svelte';
  import SettingsMenu from '$ui/lib/SettingsMenu.svelte';
  import ProfileMenu from '$ui/lib/ProfileMenu.svelte';
  import CommandPalette from '$ui/lib/CommandPalette.svelte';
  import NotificationCenter from '$ui/lib/NotificationCenter.svelte';
  import ToastHost from '$ui/lib/ToastHost.svelte';
  import PageProgress from '$ui/lib/PageProgress.svelte';

  defineRoutes([
    { path: '/', load: () => import('$ui/Home.svelte') },
    { path: '/wizard', load: () => import('$ui/Wizard.svelte') },
    { path: '/inventory', load: () => import('$ui/Inventory.svelte') },
    { path: '/store', load: () => import('$ui/Store.svelte') },
    // Legacy alias — Store.svelte wraps the Bright Engrams tab so existing
    // /eververse bookmarks still land on the right content.
    { path: '/eververse', load: () => import('$ui/Store.svelte') },
    { path: '/admin', load: () => import('$ui/Admin.svelte') },
  ]);

  const Comp = $derived(route.component);
  const banner = $derived(flagString('banner_message', ''));

  // Build identity (injected at build time by vite.config define).
  const BUILD_HASH: string = __BUILD_HASH__;
  const BUILD_DATE: string = __BUILD_DATE__;
  const buildShort = BUILD_DATE.slice(0, 10);
  const year = new Date().getFullYear();

  // API health pulse — flips green when the most recent whoami call
  // succeeded. session.user is null both pre-login and on API failure;
  // we resolve to "ok" if the user is signed in (whoami succeeded) and
  // "unknown" otherwise so signed-out browsers don't show a red dot.
  const apiState: 'ok' | 'unknown' = $derived(session.user ? 'ok' : 'unknown');

  // Manifest freshness check — compares the locally bundled pointer
  // version against the upstream version observed by the Worker's
  // hourly cron. Fires once per app load, after the rest of the UI is
  // up, so it never blocks first paint. Silent on any error — the
  // badge only appears when we have confident evidence of drift.
  let manifestStale = $state(false);
  let manifestVersion = $state<string | null>(null);
  let manifestCheckedAt = $state<number | null>(null);
  $effect(() => {
    void (async () => {
      try {
        const [localRes, upstreamRes] = await Promise.all([
          fetch('/data/manifest.json'),
          fetch('/v1/public/manifest-status').catch(() => null),
        ]);
        if (!localRes.ok) return;
        const local = (await localRes.json()) as { version?: string };
        manifestVersion = local.version ?? null;
        if (!upstreamRes || !upstreamRes.ok) return;
        const status = (await upstreamRes.json()) as {
          upstreamVersion?: string | null;
          lastCheckAt?: number | null;
        };
        manifestCheckedAt = typeof status.lastCheckAt === 'number' ? status.lastCheckAt : null;
        if (local.version && status.upstreamVersion && local.version !== status.upstreamVersion) {
          manifestStale = true;
        }
      } catch {
        // Network/dev environment — leave defaults.
      }
    })();
  });

  function manifestAgeLabel(at: number | null): string | null {
    if (at === null) return null;
    const ms = Date.now() - at;
    if (ms < 0) return null;
    const m = Math.round(ms / 60_000);
    if (m < 60) return `${m}m`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h`;
    const d = Math.round(h / 24);
    return `${d}d`;
  }

  function skipToMain(): void {
    const el = document.getElementById('main');
    if (el) {
      el.focus({ preventScroll: false });
      el.scrollIntoView({ block: 'start', behavior: 'auto' });
    }
  }

  function openPalette(): void {
    const fn = (window as unknown as { __cryptarchOpenPalette?: () => void })
      .__cryptarchOpenPalette;
    fn?.();
  }
</script>

<PageProgress />
<CommandPalette />
<ToastHost />

<button type="button" class="skip-link" onclick={skipToMain}>skip to main content</button>

<header class="topbar">
  <a class="brand" href="#/">
    <span class="logo" aria-hidden="true">◆</span>
    <strong>cryptarch</strong>
    <span class="tag">decode</span>
  </a>
  <nav>
    <a href="#/" class:active={route.path === '/'}>home</a>
    <a href="#/wizard" class:active={route.path === '/wizard'}>build</a>
    <a href="#/inventory" class:active={route.path === '/inventory'}>inventory</a>
    <a href="#/store" class:active={route.path === '/store' || route.path === '/eververse'}>store</a>
    {#if session.isAdmin}
      <a href="#/admin" class:active={route.path === '/admin'} class="admin-link">admin</a>
    {/if}
  </nav>
  <div class="topbar-right">
    <button
      type="button"
      class="cmd-trigger"
      onclick={openPalette}
      aria-label="open command palette"
      title="open command palette (⌘/Ctrl+K)"
    >
      <span class="cmd-label">search</span>
      <kbd>⌘K</kbd>
    </button>
    <SettingsMenu />
    <NotificationCenter />
    <ProfileMenu />
  </div>
</header>

{#if banner}
  <div class="site-banner" role="status">
    <span>{banner}</span>
  </div>
{/if}

<main id="main" tabindex="-1">
  {#if Comp}
    {#key route.path}
      <div in:fade={{ duration: 160 }}>
        <Comp />
      </div>
    {/key}
  {:else}
    <p class="loading">loading…</p>
  {/if}
</main>

<footer class="footstamp">
  <span class="left">
    <span class="status-dot s-{apiState}" aria-hidden="true"></span>
    <span class="status-label">api {apiState}</span>
    {#if manifestStale}
      <span class="sep">·</span>
      <span
        class="manifest-stale"
        title="local manifest version {manifestVersion ??
          '?'} is behind upstream — a redeploy will refresh it"
      >
        manifest stale
      </span>
    {:else if manifestCheckedAt}
      {@const age = manifestAgeLabel(manifestCheckedAt)}
      {#if age}
        <span class="sep">·</span>
        <span
          class="manifest-age"
          title="last upstream check {new Date(
            manifestCheckedAt,
          ).toLocaleString()} · version {manifestVersion ?? '?'}"
        >
          manifest {age} old
        </span>
      {/if}
    {/if}
    <span class="sep">·</span>
    © {year} cryptarch · not affiliated with bungie
  </span>
  <span class="right">
    <button type="button" class="kb-help" onclick={openPalette} aria-label="keyboard shortcuts">
      <kbd>⌘K</kbd> shortcuts
    </button>
    <span class="sep" aria-hidden="true">·</span>
    <code class="build" title="build {BUILD_HASH} · {BUILD_DATE}">{BUILD_HASH} ({buildShort})</code>
  </span>
</footer>

<style>
  .topbar {
    display: flex;
    align-items: center;
    gap: var(--space-6);
    padding: var(--space-3) var(--space-6);
    border-bottom: 1px solid var(--border);
    background: var(--bg-elev-1);
    position: sticky;
    top: 0;
    z-index: 50;
    backdrop-filter: blur(8px);
  }
  .skip-link {
    position: absolute;
    top: -100px;
    left: var(--space-3);
    z-index: 1000;
    background: var(--accent);
    color: var(--bg);
    padding: var(--space-2) var(--space-4);
    font-family: var(--font-mono);
    font-size: var(--text-xs);
    letter-spacing: 0.18em;
    text-transform: uppercase;
    border: 0;
    border-radius: var(--radius-2);
    font-weight: 700;
    text-decoration: none;
    cursor: pointer;
    transition: top var(--speed-fast) var(--easing);
  }
  .skip-link:focus,
  .skip-link:focus-visible {
    top: var(--space-3);
    outline: 2px solid var(--bg);
    outline-offset: 2px;
  }
  main:focus-visible {
    outline: none;
  }
  .brand {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    color: var(--fg);
  }
  .logo {
    color: var(--accent);
    font-size: 18px;
    line-height: 1;
  }
  .tag {
    font-family: var(--font-mono);
    font-size: 10px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--fg-muted);
    padding-left: var(--space-2);
    border-left: 1px solid var(--border);
  }
  nav {
    display: flex;
    gap: var(--space-2);
    font-family: var(--font-mono);
    font-size: var(--text-xs);
    letter-spacing: 0.06em;
    color: var(--fg-muted);
    text-transform: uppercase;
    flex: 1;
  }
  nav a {
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-2);
    transition:
      background var(--speed-fast) var(--easing),
      color var(--speed-fast) var(--easing);
  }
  nav a:hover {
    color: var(--fg);
    background: var(--bg-elev-2);
  }
  nav a.active {
    color: var(--accent);
    background: var(--accent-soft);
  }
  nav a.admin-link {
    color: var(--accent);
    opacity: 0.85;
  }
  nav a.admin-link:hover {
    opacity: 1;
  }
  .topbar-right {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
  }
  .cmd-trigger {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    background: var(--bg-elev-2);
    border: 1px solid var(--border);
    color: var(--fg-muted);
    padding: 4px var(--space-3);
    border-radius: var(--radius-2);
    cursor: pointer;
    font-family: var(--font-mono);
    font-size: var(--text-xs);
    text-transform: lowercase;
    transition: all var(--speed-fast) var(--easing);
  }
  .cmd-trigger:hover {
    border-color: var(--accent);
    color: var(--fg);
  }
  .cmd-trigger kbd {
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: 3px;
    padding: 1px 4px;
    font-size: 10px;
  }
  .cmd-label {
    color: var(--fg-subtle);
  }
  .site-banner {
    background: linear-gradient(90deg, rgba(255, 200, 80, 0.18), rgba(255, 200, 80, 0.06));
    border-bottom: 1px solid rgba(255, 200, 80, 0.4);
    color: var(--fg);
    padding: var(--space-2) var(--space-6);
    text-align: center;
    font-family: var(--font-mono);
    font-size: var(--text-xs);
    letter-spacing: 0.04em;
  }
  main {
    padding: var(--space-8) var(--space-6);
    max-width: 1440px;
    margin: 0 auto;
  }
  .loading {
    color: var(--fg-muted);
    font-family: var(--font-mono);
    font-size: var(--text-sm);
  }
  .footstamp {
    margin-top: var(--space-12);
    padding: var(--space-4) var(--space-6);
    border-top: 1px solid var(--border);
    color: var(--fg-subtle);
    font-family: var(--font-mono);
    font-size: 10px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-4);
    flex-wrap: wrap;
  }
  .footstamp .left,
  .footstamp .right {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
  }
  .status-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    display: inline-block;
  }
  .status-dot.s-ok {
    background: oklch(0.74 0.18 145);
    box-shadow: 0 0 8px oklch(0.74 0.18 145 / 0.6);
  }
  .status-dot.s-unknown {
    background: var(--fg-subtle);
  }
  .status-label {
    color: var(--fg-muted);
  }
  .manifest-stale {
    color: var(--accent-warning, #d9a23a);
    font-weight: 500;
    cursor: help;
  }
  .manifest-age {
    color: var(--fg-subtle);
    cursor: help;
    font-variant-numeric: tabular-nums;
  }
  .sep {
    color: var(--fg-subtle);
  }
  .kb-help {
    background: transparent;
    border: 0;
    color: var(--fg-muted);
    cursor: pointer;
    font-family: var(--font-mono);
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    padding: 0;
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
  .kb-help kbd {
    background: var(--bg-elev-1);
    border: 1px solid var(--border);
    border-radius: 3px;
    padding: 1px 4px;
    font-size: 9px;
  }
  .kb-help:hover {
    color: var(--accent);
  }
  .footstamp .build {
    background: var(--bg-elev-1);
    border: 1px solid var(--border);
    border-radius: var(--radius-2);
    padding: 1px 6px;
    color: var(--fg-muted);
    font-family: var(--font-mono);
    font-size: 10px;
    letter-spacing: 0.04em;
    text-transform: lowercase;
  }

  @media (max-width: 720px) {
    .topbar {
      gap: var(--space-2);
      padding: var(--space-3) var(--space-4);
      flex-wrap: wrap;
    }
    .brand {
      margin-right: auto;
    }
    nav {
      gap: var(--space-1);
      font-size: var(--text-xs);
      width: 100%;
      order: 3;
      padding-top: var(--space-2);
      border-top: 1px solid var(--border);
      overflow-x: auto;
      flex: 0;
    }
    nav a {
      flex-shrink: 0;
    }
    .tag {
      display: none;
    }
    .cmd-trigger .cmd-label {
      display: none;
    }
    main {
      padding: var(--space-4) var(--space-3);
    }
    .footstamp {
      padding: var(--space-3) var(--space-3);
      margin-top: var(--space-6);
    }
  }
</style>
